import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
  kstDotDateStr,
} from '@biznomi/ui-react'
import { useAuth } from '@/lib/auth/store'
import {
  deleteTraining,
  fileSizeText,
  getMissingEmployees,
  getTrainings,
  getTrainingTitles,
  openTrainingFile,
  previewTrainings,
  uploadTrainings,
  type AdminTraining,
  type TrainingMatch,
} from '@/lib/api/training'

/**
 * 교육 증빙 관리 — **관리자 + 부서장**.
 * 기획 = docs/2026-09-07_교육증빙-기획.md
 *
 *   근로계약서   관리자 + 부서장
 *   급여명세서   ADMIN 전용      ← 부서원 급여를 아는 건 권한 남용
 *   교육 증빙    관리자 + 부서장  ← 부서 교육 관리는 부서장 일이다
 *
 * ⚠ 부서장은 **담당 부서만** 본다. 서버가 거르고(getManagedDepartmentIds),
 *   화면은 온 것을 그대로 그린다. 여기서 조직 전체를 전제로 짜면 안 된다.
 *
 * 올리기·지우기는 ADMIN 만이다. 부서장은 보기만 한다.
 */

const ALL = '__all__'

/** 이수일은 date 칼럼(UTC 자정)이라 KST 로 봐도 날짜가 안 밀린다 */
function dateText(iso: string): string {
  return kstDotDateStr(iso)
}

export default function AdminTrainings() {
  const qc = useQueryClient()
  const isAdmin = useAuth((s) => s.user?.role) === 'ADMIN'

  const [title, setTitle] = useState(ALL)
  const [tab, setTab] = useState<'records' | 'missing'>('records')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AdminTraining | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')

  const { data: titlesData } = useQuery({
    queryKey: ['trainings', 'titles'],
    queryFn: getTrainingTitles,
  })
  const titles = titlesData?.success ? titlesData.data ?? [] : []

  const { data, isLoading } = useQuery({
    queryKey: ['trainings', 'admin', title],
    queryFn: () => getTrainings({ title: title === ALL ? undefined : title }),
  })
  const rows = data?.success ? data.data?.records ?? [] : []
  const total = data?.success ? data.data?.total ?? 0 : 0

  /*
   * "안 받은 사람" 은 제목을 골라야만 성립한다 — 전체로는 물음이 성립하지 않는다.
   * 그래서 제목이 ALL 이면 아예 안 부른다.
   */
  const missingTitle = title === ALL ? '' : title
  const { data: missingData, isLoading: missingLoading } = useQuery({
    queryKey: ['trainings', 'missing', missingTitle],
    queryFn: () => getMissingEmployees(missingTitle),
    enabled: tab === 'missing' && missingTitle !== '',
  })
  const missing = missingData?.success ? missingData.data ?? [] : []

  async function view(t: AdminTraining) {
    setOpening(t.id)
    setError('')
    const res = await openTrainingFile(t.id)
    setOpening(null)
    if ('error' in res) {
      // 부서장이 담당 부서 밖 기록을 열면 여기로 온다 (서버 403)
      setError(res.error)
      return
    }
    const win = window.open(res.url, '_blank')
    if (!win) window.location.href = res.url
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  async function doDelete() {
    if (!deleteTarget) return
    const res = await deleteTraining(deleteTarget.id)
    setDeleteTarget(null)
    if (!res.success) {
      setError(res.error ?? '삭제하지 못했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['trainings'] })
    setToast('교육 증빙을 지웠습니다')
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">교육 증빙</h1>
        {isAdmin && (
          <Button size="sm" onClick={() => setUploadOpen(true)}>이수증 올리기</Button>
        )}
      </div>

      <InfoBox tone="info">
        교육 이수증 PDF 를 사람별로 나눠 올려 주세요. 파일 이름 앞에 사번을 붙이면 알아서 짝을
        맞춥니다 (예: <code>D001_홍길동.pdf</code>). 교육 종류와 이수일은 회사가 정하고, 저희는
        받은 파일이 바뀌지 않았는지만 확인합니다.
      </InfoBox>

      {/*
        제목이 자유 입력이라 회사가 무엇이든 적을 수 있다. 개인 사유가 드러나는 제목
        (징계성 특별교육 같은)을 적으면 부서장이 전원 본다. 막을 일은 아니고,
        적는 사람이 알고 적으면 된다 — 그래서 여기 한 줄로 적어 둔다 (기획 §조회 권한).
      */}
      {isAdmin && (
        <p className="text-xs leading-relaxed text-slate-400">
          교육 제목은 부서장에게도 보입니다. 개인 사정이 드러나는 제목은 피해 주세요.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Select value={title} onValueChange={(v) => setTitle(v)}>
          <SelectTrigger className="w-56"><SelectValue placeholder="교육" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>전체 교육</SelectItem>
            {titles.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 탭 — 「이수한 사람」 과 「안 받은 사람」. 뒤는 사실 조회지 판정이 아니다 */}
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
        <button
          type="button"
          className={`flex-1 rounded-md px-3 py-1.5 font-medium ${tab === 'records' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
          onClick={() => setTab('records')}
        >
          이수 기록 {total > 0 ? total : ''}
        </button>
        <button
          type="button"
          className={`flex-1 rounded-md px-3 py-1.5 font-medium ${tab === 'missing' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
          onClick={() => setTab('missing')}
        >
          안 받은 사람
        </button>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {tab === 'records' ? (
        isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState
            title="아직 올라온 이수증이 없습니다"
            hint={isAdmin ? '«이수증 올리기» 로 시작하세요.' : '관리자가 올리면 여기에 뜹니다.'}
          />
        ) : (
          <ul className="divide-y divide-slate-100 rounded-card bg-white">
            {rows.map((t) => (
              <li key={t.id} className="space-y-2 px-4 py-3.5">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {t.employee.name}
                    {t.employee.employeeNumber && (
                      <span className="ml-1.5 text-xs font-normal text-slate-400">
                        {t.employee.employeeNumber}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-600">{t.title}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-400">
                    이수일 {dateText(t.completedOn)} · {t.fileName} · {fileSizeText(t.fileSize)}
                  </p>
                  {t.memo && <p className="mt-0.5 text-xs text-slate-500">{t.memo}</p>}
                </div>

                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" loading={opening === t.id} onClick={() => void view(t)}>
                    보기
                  </Button>
                  {/* 되돌리는 유일한 길이다 — 회수·취소가 없다 (status 를 안 뒀다) */}
                  {isAdmin && (
                    <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(t)}>
                      지우기
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )
      ) : missingTitle === '' ? (
        <EmptyState
          title="교육을 먼저 골라 주세요"
          hint="어떤 교육을 안 받았는지 보려면 위에서 교육을 하나 골라 주세요."
        />
      ) : missingLoading ? (
        <LoadingState />
      ) : missing.length === 0 ? (
        <EmptyState title="전원 이수했습니다" hint={`«${missingTitle}» 기록이 모두 있습니다.`} />
      ) : (
        <>
          <ul className="divide-y divide-slate-100 rounded-card bg-white">
            {missing.map((e) => (
              <li key={e.id} className="px-4 py-3">
                <p className="truncate font-medium">
                  {e.name}
                  <span className="ml-1.5 text-xs font-normal text-slate-400">
                    {[e.employeeNumber, e.department?.name].filter(Boolean).join(' · ')}
                  </span>
                </p>
              </li>
            ))}
          </ul>
          {/*
            판정하지 않는다. "받아야 하는데 안 받았다" 고 말하지 않고, 기한도 안 본다.
            그냥 이 제목으로 올라온 기록이 없는 재직자다 (기획 §안 만드는 것).
          */}
          <p className="text-xs leading-relaxed text-slate-400">
            «{missingTitle}» 이수증이 아직 안 올라온 재직자 {missing.length}명입니다. 기한이 언제인지,
            누가 받아야 하는지는 저희가 보지 않습니다.
          </p>
        </>
      )}

      {uploadOpen && (
        <UploadDialog
          titles={titles}
          onClose={() => setUploadOpen(false)}
          onDone={(msg) => {
            setUploadOpen(false)
            qc.invalidateQueries({ queryKey: ['trainings'] })
            setToast(msg)
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="교육 증빙을 지울까요?"
          message={`${deleteTarget.employee.name} 님 «${deleteTarget.title}» 기록을 지웁니다. 파일도 같이 지웁니다. 되돌릴 수 없습니다.`}
          confirmText="지우기"
          destructive
          onConfirm={() => void doDelete()}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

/**
 * 올리기 — 급여명세서와 **같은 2단계**다.
 *   ① 파일 고르기 → 서버에 **이름만** 보내 누구에게 갈지 확인
 *   ② 확인 후 실제 업로드
 *
 * 기획에 "급여만큼 민감하지 않으니 이 단계를 뺄지는 만들면서 정한다" 고 적었다.
 * **안 뺀다.** 잘못 붙은 이수증은 남의 교육 이력이 되고, 그건 되돌려도 감사 로그에
 * 남는 종류의 사고다. 그리고 사번 매칭은 실제로 틀릴 수 있다 — 이름이 다르게 적힌
 * 파일이 오면 확인 화면에서만 걸린다.
 */
function UploadDialog({
  titles,
  onClose,
  onDone,
}: {
  titles: string[]
  onClose: () => void
  onDone: (message: string) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  /*
   * 제목은 **고르거나 새로 적는다.** 고르기만 두면 첫 교육을 못 올리고,
   * 적기만 두면 «2026년 성희롱 예방교육» 과 «성희롱예방교육(2026)» 이 갈려서
   * "안 받은 사람" 이 전원으로 나온다 (기획 §title).
   */
  const [pickedTitle, setPickedTitle] = useState<string>(titles[0] ?? '')
  const [newTitle, setNewTitle] = useState('')
  const [writing, setWriting] = useState(titles.length === 0)
  const [completedOn, setCompletedOn] = useState('')
  const [memo, setMemo] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [matches, setMatches] = useState<TrainingMatch[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const title = writing ? newTitle.trim() : pickedTitle

  /** 무엇이든 바뀌면 확인 결과를 버린다 — 옛 확인으로 올리면 안 된다 */
  function reset() {
    setMatches(null)
    setError('')
  }

  async function check() {
    if (!title) return setError('교육 제목을 적어 주세요')
    if (!completedOn) return setError('이수일을 골라 주세요')
    if (files.length === 0) return setError('PDF 파일을 선택해 주세요')

    setBusy(true)
    setError('')
    const res = await previewTrainings(files.map((f) => f.name))
    setBusy(false)
    if (!res.success || !res.data) {
      setError(res.error ?? '확인하지 못했습니다')
      return
    }
    setMatches(res.data)
  }

  async function send() {
    if (!matches) return
    const okNames = new Set(matches.filter((m) => m.employeeId).map((m) => m.fileName))
    const toSend = files.filter((f) => okNames.has(f.name))
    if (toSend.length === 0) return setError('올릴 수 있는 파일이 없습니다')

    setBusy(true)
    setError('')
    const res = await uploadTrainings({ title, completedOn, memo: memo || undefined, files: toSend })
    setBusy(false)
    if (!res.success || !res.data) {
      setError(res.error ?? '올리지 못했습니다')
      return
    }
    const d = res.data
    const parts = [`«${title}» ${d.created.length}건 올렸습니다`]
    if (d.failed.length > 0) parts.push(`실패 ${d.failed.length}건`)
    onDone(parts.join(' · '))
  }

  const matched = matches?.filter((m) => m.employeeId) ?? []
  const failed = matches?.filter((m) => !m.employeeId) ?? []

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogTitle>교육 이수증 올리기</DialogTitle>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="training-title">교육</Label>
            {writing ? (
              <Input
                id="training-title"
                placeholder="예: 2026년 성희롱 예방교육"
                value={newTitle}
                onChange={(e) => { setNewTitle(e.target.value); reset() }}
              />
            ) : (
              <Select value={pickedTitle} onValueChange={(v) => { setPickedTitle(v); reset() }}>
                <SelectTrigger><SelectValue placeholder="교육 고르기" /></SelectTrigger>
                <SelectContent>
                  {titles.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {titles.length > 0 && (
              <button
                type="button"
                className="text-xs text-primary underline"
                onClick={() => { setWriting((w) => !w); reset() }}
              >
                {writing ? '이미 올린 교육에서 고르기' : '새 교육 제목 적기'}
              </button>
            )}
            {writing && titles.length > 0 && (
              <p className="text-xs text-slate-400">
                같은 교육이면 전에 쓴 제목과 똑같이 적어 주세요. 조금이라도 다르면 다른 교육으로
                갈려서 «안 받은 사람» 이 어긋납니다.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="training-date">이수일</Label>
            {/* 네이티브 date input 을 쓴다 — iOS 에서 커스텀 피커가 안 뜨는 사고를 겪었다 */}
            <Input
              id="training-date"
              type="date"
              value={completedOn}
              onChange={(e) => { setCompletedOn(e.target.value); reset() }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="training-files">파일 (PDF 여러 개, 1건당 최대 5MB)</Label>
            <input
              id="training-files"
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
              onChange={(e) => { setFiles(Array.from(e.target.files ?? [])); reset() }}
            />
            <p className="text-xs text-slate-400">
              파일 이름 앞에 사번을 붙여 주세요. 예: <code>D001_홍길동.pdf</code>
            </p>
            {files.length > 0 && !matches && (
              <p className="text-xs text-slate-500">{files.length}개 골랐습니다</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="training-memo">메모 (선택)</Label>
            <Input
              id="training-memo"
              placeholder="예: 외부 위탁교육"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </div>

          {/* ① 확인 단계 */}
          {!matches && (
            <Button className="w-full" loading={busy} disabled={files.length === 0} onClick={() => void check()}>
              누구 것인지 확인
            </Button>
          )}

          {/* ② 확인 결과 */}
          {matches && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="info">올릴 수 있는 {matched.length}건</Badge>
                {failed.length > 0 && <Badge variant="destructive">짝 못 찾은 {failed.length}건</Badge>}
              </div>

              <ul className="max-h-52 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2 text-xs">
                {matches.map((m) => (
                  <li key={m.fileName} className="flex items-start gap-2">
                    <span className={m.employeeId ? 'text-primary' : 'text-[var(--color-danger)]'}>
                      {m.employeeId ? '✓' : '✕'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-500">{m.fileName}</span>
                      <span className={m.employeeId ? 'font-medium' : 'text-[var(--color-danger)]'}>
                        {m.employeeId
                          ? `→ ${m.employeeName}${m.employeeNumber ? ` (${m.employeeNumber})` : ''}`
                          : m.reason}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              {failed.length > 0 && (
                <p className="text-xs text-slate-500">
                  짝을 못 찾은 {failed.length}건은 안 올립니다. 파일 이름을 고쳐서 다시 올려 주세요.
                </p>
              )}

              {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={reset} disabled={busy}>
                  다시 고르기
                </Button>
                <Button
                  className="flex-1"
                  loading={busy}
                  disabled={matched.length === 0}
                  onClick={() => void send()}
                >
                  {matched.length}건 올리기
                </Button>
              </div>
            </div>
          )}

          {!matches && error && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>
          )}

          {!matches && (
            <Button variant="secondary" className="w-full" onClick={onClose} disabled={busy}>
              취소
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
